/**
 * shared state machinery for the supplier-onboarding "pick from a list of
 * enum kinds plus a `none of the above` sentinel" forms (operations,
 * industries). consumers layer step-specific actions on top of the common
 * reducer returned here.
 */

export type EnumPickerCommonAction<TKind extends string> =
  | { type: 'add'; kind: TKind }
  | { type: 'remove'; kind: TKind }
  | { type: 'selectNone' }
  | { type: 'clearNone' }
  | { type: 'submitError'; message: string | null }

export type EnumPickerState<
  TKind extends string,
  TRow extends { selected: boolean }
> = Record<TKind, TRow> & { submitError: string | null }

export type EnumPickerDerived<TKind extends string> = {
  noneSelected: boolean
  selectedReals: TKind[]
  incompleteCount: number
  isValid: boolean
}

export type EnumPickerConfig<
  TKind extends string,
  TApiRow,
  TRow extends { selected: boolean }
> = {
  kinds: ReadonlyArray<TKind>
  noneKind: TKind
  empty: () => TRow
  hydrate: (row: TApiRow) => { kind: TKind; row: TRow }
  isIncomplete: (row: TRow) => boolean
}

export function createEnumPicker<
  TKind extends string,
  TApiRow,
  TRow extends { selected: boolean }
>(config: EnumPickerConfig<TKind, TApiRow, TRow>) {
  const { kinds, noneKind, empty, hydrate, isIncomplete } = config
  const realKinds = kinds.filter(k => k !== noneKind)

  function initialState(rows: TApiRow[]): EnumPickerState<TKind, TRow> {
    const state = { submitError: null } as EnumPickerState<TKind, TRow>
    for (const k of kinds) {
      state[k] = empty() as EnumPickerState<TKind, TRow>[TKind]
    }
    for (const row of rows) {
      const { kind, row: rowState } = hydrate(row)
      state[kind] = rowState as EnumPickerState<TKind, TRow>[TKind]
    }
    return state
  }

  function reduceCommon(
    state: EnumPickerState<TKind, TRow>,
    action: EnumPickerCommonAction<TKind>
  ): EnumPickerState<TKind, TRow> {
    switch (action.type) {
      case 'add':
        return {
          ...state,
          [action.kind]: { ...state[action.kind], selected: true },
          [noneKind]: { ...state[noneKind], selected: false }
        }
      case 'remove':
        return { ...state, [action.kind]: empty() }
      case 'selectNone': {
        const next = {
          ...state,
          [noneKind]: { ...empty(), selected: true }
        } as EnumPickerState<TKind, TRow>
        for (const k of realKinds) {
          next[k] = empty() as EnumPickerState<TKind, TRow>[TKind]
        }
        return next
      }
      case 'clearNone':
        return {
          ...state,
          [noneKind]: { ...state[noneKind], selected: false }
        }
      case 'submitError':
        return { ...state, submitError: action.message }
    }
  }

  function derive(
    state: EnumPickerState<TKind, TRow>
  ): EnumPickerDerived<TKind> {
    const noneSelected = state[noneKind].selected
    const selectedReals = realKinds.filter(k => state[k].selected)
    const incompleteCount = selectedReals.filter(k =>
      isIncomplete(state[k])
    ).length
    const isValid =
      noneSelected || (selectedReals.length >= 1 && incompleteCount === 0)
    return { noneSelected, selectedReals, incompleteCount, isValid }
  }

  return { realKinds, initialState, reduceCommon, derive }
}

/**
 * "0 of 22 selected" / "3 of 5 complete" / "5 complete" / "None of the above"
 * footer text shared by picker forms.
 */
export function pickerFooterLabel<TKind extends string>(
  derived: EnumPickerDerived<TKind>,
  total: number
): string {
  if (derived.noneSelected) return 'None of the above'
  if (derived.selectedReals.length === 0) return `0 of ${total} selected`
  if (derived.incompleteCount > 0) {
    return `${derived.selectedReals.length - derived.incompleteCount} of ${derived.selectedReals.length} complete`
  }
  return `${derived.selectedReals.length} complete`
}
